import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { convertToInlineCss } from '@atlaskit/editor-common/css-helper';
import type { NextEditorPlugin } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';
import { token } from '@atlaskit/tokens';

const pluginName = 'aiPlannerRemoteTextHighlight';
const pmPluginName = 'aiPlannerRemoteTextHighlightPmPlugin';
const pluginMetaKey = pluginName;
const HIGHLIGHT_DURATION_MS = 10000;

export type AIPlannerBridgeHighlightRange = {
	from: number;
	to: number;
};

type RemoteTextHighlight = {
	expiresAt: number;
	id: string;
	ranges: AIPlannerBridgeHighlightRange[];
};

type AIPlannerBridgeHighlightMeta = {
	now: number;
	type: 'expire';
};

type AIPlannerBridgeHighlightPluginState = {
	decorations: DecorationSet;
	highlights: RemoteTextHighlight[];
};

export const aiPlannerRemoteTextHighlightPluginKey: PluginKey<AIPlannerBridgeHighlightPluginState> =
	new PluginKey<AIPlannerBridgeHighlightPluginState>(pluginName);

const aiPlannerBridgeHighlightStyle = convertToInlineCss({
	backgroundColor: token('color.background.accent.purple.subtlest'),
	textDecoration: 'underline',
	textDecorationColor: token('color.border.accent.purple'),
	textDecorationStyle: 'dashed',
	textDecorationThickness: token('border.width'),
});

const isExpiryMeta = (meta: unknown): meta is AIPlannerBridgeHighlightMeta =>
	Boolean(
		meta &&
		typeof meta === 'object' &&
		(meta as AIPlannerBridgeHighlightMeta).type === 'expire' &&
		typeof (meta as AIPlannerBridgeHighlightMeta).now === 'number',
	);

const getMeta = (tr: Transaction): AIPlannerBridgeHighlightMeta | undefined => {
	const meta = tr.getMeta(pluginMetaKey);
	return isExpiryMeta(meta) ? meta : undefined;
};

export const mergeResolvedHighlightRanges = (
	ranges: AIPlannerBridgeHighlightRange[],
): AIPlannerBridgeHighlightRange[] => {
	const sortedRanges = [...ranges].sort((left, right) => left.from - right.from);

	return sortedRanges.reduce<AIPlannerBridgeHighlightRange[]>((accumulator, range) => {
		const previousRange = accumulator[accumulator.length - 1];

		if (!previousRange) {
			accumulator.push({ ...range });
			return accumulator;
		}

		if (range.from <= previousRange.to + 1) {
			previousRange.to = Math.max(previousRange.to, range.to);
			return accumulator;
		}

		accumulator.push({ ...range });
		return accumulator;
	}, []);
};

const getTextRangesBetween = (
	doc: PMNode,
	from: number,
	to: number,
): AIPlannerBridgeHighlightRange[] => {
	const ranges: AIPlannerBridgeHighlightRange[] = [];

	doc.nodesBetween(from, to, (node, pos) => {
		if (!node.isText) {
			return true;
		}

		const textFrom = Math.max(from, pos);
		const textTo = Math.min(to, pos + node.nodeSize);

		if (textTo > textFrom) {
			ranges.push({ from: textFrom, to: textTo });
		}

		return false;
	});

	return ranges;
};

export const getInsertedTextRangesFromRemoteTransaction = (
	tr: Transaction,
	doc: PMNode,
): AIPlannerBridgeHighlightRange[] => {
	if (!tr.docChanged || !tr.getMeta('isRemote') || tr.getMeta('replaceDocument')) {
		return [];
	}

	const insertedRanges: AIPlannerBridgeHighlightRange[] = [];

	tr.mapping.maps.forEach((stepMap, index) => {
		stepMap.forEach((oldStart, oldEnd, newStart, newEnd) => {
			const oldSize = oldEnd - oldStart;
			const newSize = newEnd - newStart;

			if (newSize <= 0 || newSize <= oldSize) {
				return;
			}

			const remainingMapping = tr.mapping.slice(index + 1);
			const from = remainingMapping.map(newStart, 1);
			const to = remainingMapping.map(newEnd, -1);

			insertedRanges.push(...getTextRangesBetween(doc, from, to));
		});
	});

	return mergeResolvedHighlightRanges(insertedRanges);
};

const mapHighlightRanges = (
	ranges: AIPlannerBridgeHighlightRange[],
	tr: Transaction,
): AIPlannerBridgeHighlightRange[] =>
	mergeResolvedHighlightRanges(
		ranges
			.map(({ from, to }) => ({
				from: tr.mapping.map(from, 1),
				to: tr.mapping.map(to, -1),
			}))
			.filter(({ from, to }) => to > from),
	);

const buildDecorations = (state: EditorState, highlights: RemoteTextHighlight[]): DecorationSet => {
	const decorations: Decoration[] = highlights.flatMap((highlight) =>
		highlight.ranges.map(({ from, to }, index) =>
			Decoration.inline(
				from,
				to,
				{
					class: 'ai-in-editor-ai-content',
					'data-testid': 'ai-in-editor-ai-content',
					style: aiPlannerBridgeHighlightStyle,
				},
				{ key: `aiPlannerRemoteTextHighlight:${highlight.id}:${index}:${from}:${to}` },
			),
		),
	);

	return decorations.length > 0
		? DecorationSet.create(state.doc, decorations)
		: DecorationSet.empty;
};

const removeExpiredHighlights = (highlights: RemoteTextHighlight[], now: number) => {
	const filteredHighlights = highlights.filter(({ expiresAt }) => expiresAt > now);
	return filteredHighlights.length === highlights.length ? highlights : filteredHighlights;
};

const buildPluginState = (
	state: EditorState,
	highlights: RemoteTextHighlight[],
): AIPlannerBridgeHighlightPluginState => ({
	decorations: buildDecorations(state, highlights),
	highlights,
});

const scheduleNextExpiry = (view: EditorView, timeoutIdRef: { current: number | undefined }) => {
	if (timeoutIdRef.current !== undefined) {
		window.clearTimeout(timeoutIdRef.current);
		timeoutIdRef.current = undefined;
	}

	const pluginState = aiPlannerRemoteTextHighlightPluginKey.getState(view.state);
	const nextExpiry = pluginState?.highlights.reduce<number | undefined>(
		(earliest, { expiresAt }) =>
			earliest === undefined || expiresAt < earliest ? expiresAt : earliest,
		undefined,
	);

	if (nextExpiry === undefined) {
		return;
	}

	timeoutIdRef.current = window.setTimeout(
		() => {
			timeoutIdRef.current = undefined;
			const now = Date.now();
			view.dispatch(view.state.tr.setMeta(pluginMetaKey, { type: 'expire', now }));
		},
		Math.max(0, nextExpiry - Date.now()),
	);
};

export const aiPlannerRemoteTextHighlightPlugin: NextEditorPlugin<typeof pluginName> = () => {
	return {
		name: pluginName,
		pmPlugins() {
			return [
				{
					name: pmPluginName,
					plugin() {
						return new SafePlugin({
							key: aiPlannerRemoteTextHighlightPluginKey,
							state: {
								init: (_config: unknown, state: EditorState) => buildPluginState(state, []),
								apply(
									tr: Transaction,
									pluginState: AIPlannerBridgeHighlightPluginState,
									_oldState: EditorState,
									newState: EditorState,
								) {
									const meta = getMeta(tr);
									const now = meta?.now ?? Date.now();
									let highlights = removeExpiredHighlights(pluginState.highlights, now);

									if (tr.docChanged) {
										highlights = highlights
											.map((highlight) => ({
												...highlight,
												ranges: mapHighlightRanges(highlight.ranges, tr),
											}))
											.filter(({ ranges }) => ranges.length > 0);
									}

									const insertedRanges = getInsertedTextRangesFromRemoteTransaction(
										tr,
										newState.doc,
									);

									if (insertedRanges.length > 0) {
										highlights = [
											...highlights,
											{
												id: `${now}:${highlights.length}:${insertedRanges[0].from}`,
												ranges: insertedRanges,
												expiresAt: now + HIGHLIGHT_DURATION_MS,
											},
										];
									}

									if (highlights === pluginState.highlights && !meta && !tr.docChanged) {
										return pluginState;
									}

									return buildPluginState(newState, highlights);
								},
							},
							props: {
								decorations(state: EditorState) {
									return aiPlannerRemoteTextHighlightPluginKey.getState(state)?.decorations;
								},
							},
							view(view: EditorView) {
								const timeoutIdRef: { current: number | undefined } = { current: undefined };
								scheduleNextExpiry(view, timeoutIdRef);

								return {
									update(updatedView: EditorView) {
										scheduleNextExpiry(updatedView, timeoutIdRef);
									},
									destroy() {
										if (timeoutIdRef.current !== undefined) {
											window.clearTimeout(timeoutIdRef.current);
										}
									},
								};
							},
						});
					},
				},
			];
		},
	};
};
